import React, { useState } from 'react';
import {
  Box, IconButton, Tooltip, Typography,
  Paper, Grid, Divider, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import {
  useReactTable,
  getCoreRowModel,
  getExpandedRowModel,
  flexRender
} from '@tanstack/react-table';
import { format, parseISO } from 'date-fns';
import DataTimeDisplay from './DataTimeDisplay';
import ScheduleBadge from './ScheduleBadge';
import ScheduleDetails from './ScheduleDetails';
import StatusChip from './StatusChip';
import JobActions from './JobActions';
import JobDetails from './JobDetails';

export const JobsList = ({ jobs, onEdit, onRunNow, onToggleStatus, onDelete }) => {
  
  const columns = [
    {
      id: 'expand',
      header: () => null,
      width: '40px',
      cell: ({ row }) => (
        <Button 
          onClick={() => row.toggleExpanded()}
          variant="text"
          size="small"
        >
          {row.getIsExpanded() ? 'Hide' : 'Show'} Details
        </Button>
      ),
    },
    {
      id: 'name',
      header: 'Job Name',
      cell: ({ row }) => (
        <Box>
          <Typography variant="subtitle2">{row.original.name}</Typography>
          {/* <Typography variant="caption" color="text.secondary">
            {row.original.description || 'No description'}
          </Typography> */}
        </Box>
      ),
    },
    {
      id: 'source',
      header: 'Data Source',
      cell: ({ row }) => (
        <Box>
          <Typography variant="subtitle2">{row.original.dataSource}</Typography>
          <Typography variant="caption" color="text.secondary">
            {row.original.sourceEndpoint}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'schedule',
      header: 'Schedule',
      cell: ({ row }) => <ScheduleBadge config={row.original.scheduleConfig} />,
    },
    {
      id: 'status',
      header: 'Status',
      cell: ({ row }) => <StatusChip status={row.original.status} />,
    },
    {
      id: 'lastModified',
      header: 'Last Modified',
      cell: ({ row }) => {
        return <Typography variant="subtitle2">
          {format(parseISO(row.original.lastModified), 'PPp')}
        </Typography>
      }
    },
    {
      id: 'actions',
      header: '',
      width: '40px',
      cell: ({ row }) => (
        <JobActions 
          job={row.original}
          onEdit={onEdit}
          onRunNow={onRunNow}
          onDelete={onDelete}
          onToggleStatus={onToggleStatus}
        />
      ),
    },
  ];

  const table = useReactTable({
    data: jobs,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  });

  const renderSubRow = (row) => (
    <Box sx={{ p: 2, bgcolor: 'background.paper', ml: 4, mr: 2, mb: 2, borderRadius: 1 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Data Time Configuration</Typography>
            <Divider sx={{ my: 1 }} />
            <DataTimeDisplay config={row.original.dataTimeConfig} />
          </Box>
          
          <Box>
            <Typography variant="subtitle2">Source Filters</Typography>
            <Divider sx={{ my: 1 }} />
            {row.original.sourceFilters?.length > 0 ? (
              row.original.sourceFilters.map((filter, idx) => (
                <Box key={idx} sx={{ mb: 1 }}>
                  <Typography variant="body2" component="span" sx={{ fontWeight: 'medium' }}>
                    {filter.name}:
                  </Typography>{' '}
                  <Typography variant="body2" component="span">
                    {filter.values.join(', ')}
                  </Typography>
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No filters applied
              </Typography>
            )}
          </Box>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Schedule Configuration</Typography>
            <Divider sx={{ my: 1 }} />
            <ScheduleDetails config={row.original.scheduleConfig} />
          </Box>
          
          <Box>
            <Typography variant="subtitle2">Additional Information</Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2">
              Created by: {row.original.createdBy}
            </Typography>
            <Typography variant="body2">
              Created at: {format(parseISO(row.original.createdAt), 'PPp')}
            </Typography>
            {row.original.settings && (
              <Typography variant="body2">
                Custom settings: {JSON.stringify(JSON.parse(row.original.settings), null, 2)}
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );

  return (
      
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ width: '100%', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id} style={{ backgroundColor: '#f5f5f5'}}>
                {headerGroup.headers.map(header => (
                  <th 
                    key={header.id}
                    style={{ 
                      padding: '16px', 
                      textAlign: 'left', 
                      borderBottom: '1px solid rgba(224, 224, 224, 1)',
                      fontSize: '14px',
                      width: header.column.columnDef.width || 'auto'
                    }}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => (
              <React.Fragment key={row.id}>
                <tr>
                  {row.getVisibleCells().map(cell => (
                    <td 
                      key={cell.id}
                      style={{ 
                        padding: '16px', 
                        borderBottom: '1px solid rgba(224, 224, 224, 1)' 
                      }}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </td>
                  ))}
                </tr>
                {row.getIsExpanded() && (
                  <tr style={{ backgroundColor: '#fafafa' }}>
                    <td colSpan={columns.length} style={{ padding: 0 }}>
                      <Box sx={{ mx: 1, mb: 1, p: 2, border: '1px solid #ddd', borderRadius: 1, boxShadow: 1, backgroundColor: '#fff' }}>
                        <JobDetails job={row.original} />
                      </Box>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </Box>
    </Paper>
  );
};